import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Toaster, toast } from 'sonner';
import { Star, ArrowLeft, CircleNotch } from '@phosphor-icons/react';
import { useNavigate } from 'react-router-dom';
import { ExportButton } from '../components/ExportCSV';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Window29DigitPosition() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);

  useEffect(() => {
    (async () => {
      try { const res = await axios.get(`${API}/analysis/digit-position`); setData(res.data); }
      catch { toast.error('Failed to fetch digit position'); }
      finally { setLoading(false); }
    })();
  }, []);

  const ballLabels = { ball_1: 'Ball 1', ball_2: 'Ball 2', ball_3: 'Ball 3', ball_4: 'Ball 4', ball_5: 'Ball 5' };

  return (
    <div className="min-h-screen bg-gray-50">
      <Toaster position="top-right" richColors />
      <header className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3">
              <Button variant="ghost" size="sm" onClick={() => navigate('/')} data-testid="back-btn"><ArrowLeft size={18} /></Button>
              <div className="w-10 h-10 bg-[#002FA7] rounded flex items-center justify-center"><Star size={24} weight="fill" className="text-amber-400" /></div>
              <div><h1 className="text-2xl font-bold text-gray-900">Window 29</h1><p className="text-xs text-gray-500">First/Last Digit Position Analysis</p></div>
            </div>
          </div>

            <ExportButton data={data} filename="w29_digit_position" />
          </div>
      </header>
      <main className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {loading ? <div className="flex justify-center py-20"><CircleNotch size={32} className="animate-spin text-[#002FA7]" /></div> : data ? (
          <Tabs defaultValue="ball_1" className="space-y-4">
            <TabsList className="bg-white border border-gray-200 p-1 flex-wrap">
              {Object.entries(ballLabels).map(([k, l]) => (
                <TabsTrigger key={k} value={k} className="data-[state=active]:bg-[#002FA7] data-[state=active]:text-white font-bold" data-testid={`tab-${k}`}>{l}</TabsTrigger>
              ))}
            </TabsList>
            {Object.entries(ballLabels).map(([key, label]) => {
              const bd = data.balls?.[key] || {};
              return (
                <TabsContent key={key} value={key}>
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    <div className="bg-white border rounded p-4">
                      <h3 className="font-bold text-gray-900 mb-3">{label} — First Digit (Tens place)</h3>
                      <p className="text-xs text-gray-500 mb-2">0=single digit (1-9), 1=teens (10-19), etc.</p>
                      <div className="space-y-2" data-testid={`first-digits-${key}`}>
                        {(bd.first_digits || []).map(fd => {
                          const max = Math.max(...(bd.first_digits || []).map(x => x.count), 1);
                          return (
                            <div key={fd.digit} className="flex items-center gap-2">
                              <span className="font-mono text-lg w-8 text-right font-bold">{fd.digit}</span>
                              <div className="flex-1 bg-gray-100 rounded h-6 overflow-hidden relative">
                                <div className="h-full bg-[#002FA7] rounded" style={{ width: `${(fd.count / max) * 100}%` }} />
                                <span className="absolute right-2 top-0.5 text-[10px] font-mono font-bold">{fd.count}x ({fd.pct}%)</span>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                    <div className="bg-white border rounded p-4">
                      <h3 className="font-bold text-gray-900 mb-3">{label} — Last Digit (Units place)</h3>
                      <div className="space-y-2" data-testid={`last-digits-${key}`}>
                        {(bd.last_digits || []).map(ld => {
                          const max = Math.max(...(bd.last_digits || []).map(x => x.count), 1);
                          return (
                            <div key={ld.digit} className="flex items-center gap-2">
                              <span className="font-mono text-lg w-8 text-right font-bold">{ld.digit}</span>
                              <div className="flex-1 bg-gray-100 rounded h-6 overflow-hidden relative">
                                <div className={`h-full rounded ${ld.digit === 0 || ld.digit === 5 ? 'bg-amber-500' : 'bg-blue-400'}`} style={{ width: `${(ld.count / max) * 100}%` }} />
                                <span className="absolute right-2 top-0.5 text-[10px] font-mono font-bold">{ld.count}x ({ld.pct}%)</span>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                </TabsContent>
              );
            })}
          </Tabs>
        ) : null}
      </main>
    </div>
  );
}
